import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Download,
  Mic,
  Zap,
  AlignLeft,
  Settings,
  Sparkles,
  Clock,
  Volume2,
  Music,
  FileAudio
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../../firebase.init";


const TextToVoiceoverGenerator = () => {
  // --- AUTH & CORE STATE (UNCHANGED) ---
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingTools, setLoadingTools] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // --- FORM STATE ---
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("Zephyr");
  const [tone, setTone] = useState("Warm and friendly");
  
  // --- OUTPUT STATE ---
  const [audioUrl, setAudioUrl] = useState(null);

  // --- CREDIT & TOOL CONFIG (UNCHANGED) ---
  const [credits, setCredits] = useState(0);
  const [creditCost, setCreditCost] = useState(2);
  const [apiKeys, setApiKeys] = useState([]);

  // --- OPTIONS ---
  const MAX_CHARS = 3000;
  
  const voiceOptions = [
    { id: "Zephyr", label: "Zephyr (Warm / Natural)" },
    { id: "Aoede", label: "Aoede (Female / Professional)" },
    { id: "Charon", label: "Charon (Male / Deep)" },
    { id: "Fenrir", label: "Fenrir (Male / Energetic)" },
    { id: "Kore", label: "Kore (Female / Calm)" },
    { id: "Puck", label: "Puck (Neutral / Casual)" }
  ];

  const toneOptions = [
    "Warm and friendly",
    "Professional and authoritative",
    "Energetic and enthusiastic",
    "Calm and relaxing",
    "Dramatic and engaging",
    "Documentary style narrative"
  ];

  // Fetch API keys (Unchanged)
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allyoutube01-2d46d9aff565.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) setError("No API keys available. Please contact administrator.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch Tools List (Unchanged)
  const fetchToolsList = async () => {
    try {
      const response = await axios.get(`https://allyoutube01-2d46d9aff565.herokuapp.com/api/tools-section`);
      if (response.data && response.data.success && response.data.data && response.data.data.tools) {
        const tool = response.data.data.tools.find(t => t.id === 10); 
        if (tool) {
            setCreditCost(tool.creditCost);
        }
      }
    } catch (err) {
        console.warn("Could not fetch tools list, using default credit cost");
    } finally {
        setLoadingTools(false);
    }
  };

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Initial Data Fetch (Unchanged)
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        setLoadingTools(false);
        return;
      }
      try {
        await Promise.all([fetchApiKeys(), fetchToolsList()]);
        
        const userResponse = await axios.get(`https://allyoutube01-2d46d9aff565.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          setCredits(userData.currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // --- AUDIO PROCESSING UTILITIES (Ported from Python to JS) ---
  const base64ToArrayBuffer = (base64) => {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const convertToWav = (audioDataBuffer, mimeType) => {
    let sampleRate = 24000;
    let bitsPerSample = 16;
    
    // Parse MIME Type (e.g., audio/L16;rate=24000)
    if (mimeType.includes("rate=")) {
      const rateStr = mimeType.split("rate=")[1];
      if (rateStr) sampleRate = parseInt(rateStr);
    }
    if (mimeType.includes("L8")) bitsPerSample = 8;
    
    const numChannels = 1;
    const dataSize = audioDataBuffer.byteLength;
    const bytesPerSample = bitsPerSample / 8;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const chunkSize = 36 + dataSize;

    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, chunkSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    new Uint8Array(buffer, 44).set(new Uint8Array(audioDataBuffer));

    return buffer;
  };

  // --- GENERATION LOGIC ---
  const generateVoiceover = async () => {
    if (!text.trim()) {
      setError("Please enter some text to synthesize.");
      return;
    }
    if (text.length > MAX_CHARS) {
      setError(`Text exceeds maximum length of ${MAX_CHARS} characters.`);
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }
    if (credits < creditCost) {
      setError(`Insufficient credits. You need ${creditCost} credits to generate this content.`);
      return;
    }

    setGenerating(true);
    setError("");
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl); // Cleanup old memory
      setAudioUrl(null);
    }

    try {
      // Deduct credits (Unchanged)
      const deductResponse = await axios.post(
        `https://allyoutube01-2d46d9aff565.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: creditCost,
        }
      );
      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }
      setCredits((prev) => prev - creditCost);

      // Call AI (Using Gemini TTS API)
      const MODEL_ID = "gemini-2.5-pro-preview-tts";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      
      const payload = {
        contents: [{
          role: "user",
          parts: [{ text: `Read aloud in a ${tone} tone:\n${text}` }]
        }],
        generationConfig: {
          temperature: 1,
          responseModalities: ["AUDIO"],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                voiceName: voice
              }
            }
          }
        }
      };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      let audioParts = [];
      let currentMimeType = "audio/L16;rate=24000";

      if (data.candidates && data.candidates[0].content.parts) {
        data.candidates[0].content.parts.forEach(part => {
          if (part.inlineData && part.inlineData.data) {
            audioParts.push(part.inlineData.data);
            if (part.inlineData.mimeType) currentMimeType = part.inlineData.mimeType;
          }
        });
      }

      if (audioParts.length === 0) {
         throw new Error("No audio data returned from the API.");
      }

      // Merge base64 parts into a single buffer
      let totalLength = 0;
      const buffers = audioParts.map(b64 => {
        const buf = base64ToArrayBuffer(b64);
        totalLength += buf.byteLength;
        return new Uint8Array(buf);
      });

      const mergedBuffer = new Uint8Array(totalLength);
      let offset = 0;
      buffers.forEach(buf => {
        mergedBuffer.set(buf, offset);
        offset += buf.byteLength;
      });

      // Convert to WAV and create Blob URL
      const wavBuffer = convertToWav(mergedBuffer.buffer, currentMimeType);
      const blob = new Blob([wavBuffer], { type: "audio/wav" });
      const url = URL.createObjectURL(blob);
      
      setAudioUrl(url);
      toast.success("Voiceover generated successfully!");

    } catch (err) {
      console.error("Error generating voiceover:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);

      // Refund credits on failure (Unchanged)
      try {
        await axios.post(`https://allyoutube01-2d46d9aff565.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: creditCost,
        });
        setCredits((prev) => prev + creditCost);
      } catch (refundErr) {
        console.error("Error refunding credits:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  if (loadingAuth || loading || loadingKeys || loadingTools) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
            <div style={{ border: '4px solid #f1f5f9', borderTop: '4px solid #0ea5e9', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', margin: '0 auto 10px' }}></div>
            <p style={{ color: '#64748b', fontWeight: '500' }}>Loading Workspace...</p>
        </div>
      
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ background: 'white', padding: '3rem', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: '400px' }}>
          <Mic size={48} color="#0ea5e9" style={{ marginBottom: '1rem' }} />
          <h2 style={{ color: '#0f172a', marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '700' }}>Authentication Required</h2>
          <button onClick={() => (window.location.href = "/login")} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', width: '100%' }}>
            Proceed to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .tts-app { font-family: 'Inter', system-ui, sans-serif; background: #f8fafc; min-height: 100vh; padding: 2rem 1rem; color: #334155; }
        .saas-card { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); border: 1px solid #e2e8f0; padding: 1.75rem; height: 100%; display: flex; flex-direction: column;}
        .form-label { font-weight: 600; color: #475569; font-size: 0.9rem; margin-bottom: 0.5rem; display: block; }
        .form-control, .form-select { width: 100%; padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 0.95rem; color: #0f172a; transition: all 0.2s; margin-bottom: 0.5rem; box-sizing: border-box; }
        .form-control:focus, .form-select:focus { outline: none; border-color: #0ea5e9; box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1); background: #fff; }
        textarea.form-control { resize: vertical; min-height: 220px; line-height: 1.5; }
        
        .char-counter { font-size: 0.8rem; color: #64748b; text-align: right; margin-bottom: 1.25rem; font-weight: 500; }
        .char-counter.limit { color: #ef4444; }
        
        .gradient-btn { background: linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%); color: white; border: none; padding: 1rem 1.5rem; border-radius: 10px; font-weight: 600; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.3s ease; width: 100%; box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25); margin-top: auto;}
        .gradient-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(14, 165, 233, 0.35); }
        .gradient-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }

        .audio-player-container { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 2rem; text-align: center; margin-bottom: 1.5rem; display: flex; flex-direction: column; align-items: center;}
        audio { width: 100%; margin: 1.5rem 0; height: 50px; border-radius: 25px; outline: none; }
        audio::-webkit-media-controls-panel { background-color: #e0f2fe; }
        
        .download-btn { background: white; border: 1px solid #cbd5e1; color: #0f172a; padding: 10px 20px; border-radius: 30px; font-size: 0.9rem; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; text-decoration: none;}
        .download-btn:hover { background: #f1f5f9; border-color: #94a3b8; }
        
        @keyframes custom-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.98); } }
        .anim-pulse { animation: custom-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>

      <div className="tts-app">
        <Toaster position="top-right" />
        
        <div className="container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Top Navigation */}
          <div className="row mb-4">
            <div className="col-12 d-flex justify-content-between align-items-center" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => window.history.back()} style={{ background: "#fff", border: "1px solid #e2e8f0", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", color: "#64748b" }}>
                ← Dashboard
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "white", padding: "8px 20px", borderRadius: "30px", border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                <span style={{ fontWeight: "600", color: "#64748b", fontSize: "0.9rem" }}>Credits:</span>
                <span style={{ color: credits < creditCost ? "#ef4444" : "#10b981", fontWeight: "800", fontSize: "1.1rem" }}>{credits}</span>
                {credits < creditCost && (
                  <button onClick={() => window.location.href = "/user/select-package"} style={{ background: "#10b981", color: "white", border: "none", padding: "6px 14px", borderRadius: "20px", cursor: "pointer", fontSize: "0.8rem", fontWeight: "700" }}>
                    Refill
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-5" style={{ textAlign: "center", margin: "3rem 0" }}>
            <h1 style={{ fontSize: "2.5rem", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", letterSpacing: "-0.02em" }}>
              <Mic color="#0ea5e9" size={40} /> Text to Voiceover Generator
            </h1>
            <p style={{ color: "#64748b", marginTop: "12px", fontSize: "1.1rem", maxWidth: "650px", margin: "12px auto 0" }}>
              Convert text into realistic, studio-quality AI voiceovers for your YouTube videos, Shorts, and podcasts.
            </p>
          </div>

          {error && (
            <div style={{ background: "#fef2f2", color: "#ef4444", padding: "1rem", borderRadius: "10px", marginBottom: "2rem", textAlign: "center", border: "1px solid #fecaca", fontWeight: "600" }}>
              {error}
            </div>
          )}

          <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
            
            {/* Left Column - Input Form */}
            <div className="col-lg-5 col-md-6" style={{ flex: '1 1 400px', maxWidth: '500px' }}>
              <div className="saas-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #f1f5f9' }}>
                  <AlignLeft size={20} color="#0ea5e9" />
                  <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a', fontWeight: '700' }}>Voice Script</h3>
                </div>
                
                <label className="form-label">Text to Convert <span style={{color:'#ef4444'}}>*</span></label>
                <textarea
                  className="form-control"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Paste your script here. Add commas and periods for natural pauses..."
                />
                <div className={`char-counter ${text.length > MAX_CHARS ? 'limit' : ''}`}>
                  {text.length} / {MAX_CHARS} characters
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">AI Voice</label>
                    <select className="form-select" value={voice} onChange={(e) => setVoice(e.target.value)} style={{ marginBottom: 0 }}>
                      {voiceOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Vibe / Tone</label>
                    <select className="form-select" value={tone} onChange={(e) => setTone(e.target.value)} style={{ marginBottom: 0 }}>
                      {toneOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>

                <button onClick={generateVoiceover} disabled={generating || !text.trim() || credits < creditCost || text.length > MAX_CHARS} className="gradient-btn">
                  {generating ? <Clock size={18} className="fa-spin" /> : <Sparkles size={18} />}
                  {generating ? "Synthesizing Audio..." : `Generate Voiceover (${creditCost} Credits)`}
                </button>
              </div>
            </div>

            {/* Right Column - Result */}
            <div className="col-lg-7 col-md-6" style={{ flex: '1 1 450px' }}>
              
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h2 style={{ margin: 0, fontSize: "1.5rem", color: "#0f172a", fontWeight: "700" }}>Generated Audio</h2>
              </div>

              {generating ? (
                 <div className="saas-card anim-pulse" style={{ textAlign: "center", padding: "6rem 2rem", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '70px', height: '70px', background: '#e0f2fe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                      <Music size={34} color="#0ea5e9" />
                    </div>
                    <h3 style={{ color: '#0f172a', fontWeight: '800', marginBottom: '0.5rem' }}>Rendering Audio...</h3>
                    <p style={{ color: "#64748b", maxWidth: "350px", margin: "0 auto" }}>Applying voice models, managing inflection, and building the high-quality WAV file.</p>
                 </div>
              ) : audioUrl ? (
                <div className="saas-card" style={{ padding: '2rem' }}>
                  
                  <div className="audio-player-container">
                    <div style={{ background: '#e0f2fe', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                      <Volume2 size={30} color="#0ea5e9" />
                    </div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#0f172a', fontWeight: '700' }}>Your AI Voiceover is Ready</h3>
                    <p style={{ margin: '0', color: '#64748b', fontSize: '0.9rem' }}>Voice: {voice} | Format: WAV (High Quality)</p>
                    
                    <audio controls src={audioUrl}>
                      Your browser does not support the audio element.
                    </audio>

                    <a href={audioUrl} download="ai-voiceover-studio.wav" className="download-btn">
                      <Download size={18} /> Download Audio (.wav)
                    </a>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileAudio size={18} color="#64748b" /> Transcript Reference
                    </h4>
                    <p style={{ margin: 0, color: '#475569', fontSize: '0.95rem', lineHeight: '1.6', maxHeight: '150px', overflowY: 'auto', paddingRight: '10px' }}>
                      {text}
                    </p>
                  </div>

                </div>
              ) : (
                <div className="saas-card" style={{ textAlign: 'center', padding: '6rem 2rem', border: '2px dashed #cbd5e1', background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Volume2 size={56} color="#cbd5e1" style={{ marginBottom: '1.5rem' }} />
                  <h3 style={{ color: '#64748b', fontWeight: '700', margin: '0 0 0.5rem 0' }}>Ready to Synthesize</h3>
                  <p style={{ color: '#94a3b8', margin: 0, maxWidth: '350px' }}>Paste your script and select an AI voice to instantly generate a playable and downloadable voiceover.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TextToVoiceoverGenerator;